import React, { useState, useRef, useCallback } from 'react';
import { ConversationItem } from './ConversationItem';
import { useConversations } from '../../hooks';
import { LoadingSpinner } from '../Shared/LoadingSpinner';

interface ConversationListProps {
  currentUserId: string;
  selectedConversationId: string | null;
  onSelectConversation: (id: string) => void;
}

export const ConversationList: React.FC<ConversationListProps> = ({
  currentUserId,
  selectedConversationId,
  onSelectConversation
}) => {
  const {
    conversations,
    loading,
    loadingMore,
    error,
    hasMore,
    loadMoreConversations
  } = useConversations(currentUserId);
  const [searchTerm, setSearchTerm] = useState('');

  // Ref for infinite scroll observer
  const observerRef = useRef<IntersectionObserver | null>(null);
  const loadMoreTriggerRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (loadingMore) return;

      if (observerRef.current) {
        observerRef.current.disconnect();
      }

      observerRef.current = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && hasMore && !searchTerm) {
            loadMoreConversations();
          }
        },
        { threshold: 0.1 }
      );

      if (node) {
        observerRef.current.observe(node);
      }
    },
    [loadingMore, hasMore, loadMoreConversations, searchTerm]
  );

  const filteredConversations = conversations.filter((c) => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();

    // Determine display name (Guest vs Registered)
    let displayName = '';
    const otherParticipantId = c.participantIds.find(
      (id) => id !== currentUserId
    );

    if (c.metadata?.type === 'inquiry' && c.metadata.guestName) {
      displayName = c.metadata.guestName;
    } else if (
      otherParticipantId &&
      c.participants &&
      c.participants[otherParticipantId]
    ) {
      displayName = c.participants[otherParticipantId].displayName;
    }

    // Check display name
    if (displayName && displayName.toLowerCase().includes(term)) return true;

    // Check last message content
    if (
      c.lastMessage?.content &&
      c.lastMessage.content.toLowerCase().includes(term)
    )
      return true;

    return false;
  });

  if (loading) return <LoadingSpinner />;
  if (error)
    return <div className="p-4 text-red-500">Failed to load conversations</div>;

  return (
    <div className="flex flex-col h-full bg-white">
      <div className="p-3 md:p-4 border-b border-gray-200 bg-white sticky top-0 z-10">
        <h2 className="text-lg md:text-xl font-bold text-gray-800 mb-3">
          Messages
        </h2>
        <div className="relative">
          <input
            type="search"
            placeholder="Search conversations..."
            className="w-full pl-10 pr-4 py-2.5 md:py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-50 transition-all"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <svg
            className="absolute left-3 top-3 md:top-2.5 h-5 w-5 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {filteredConversations.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            No conversations found.
          </div>
        ) : (
          <>
            {filteredConversations.map((conversation) => (
              <ConversationItem
                key={conversation.id}
                conversation={conversation}
                isSelected={selectedConversationId === conversation.id}
                currentUserId={currentUserId}
                onSelect={onSelectConversation}
              />
            ))}

            {/* Load more trigger element */}
            {hasMore && !searchTerm && (
              <div ref={loadMoreTriggerRef} className="p-4 flex justify-center">
                {loadingMore ? (
                  <div className="flex items-center gap-2 text-gray-500">
                    <svg
                      className="animate-spin h-5 w-5"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    <span className="text-sm">Loading more...</span>
                  </div>
                ) : (
                  <div className="h-4" />
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};
